import { z } from "zod";

/**
 * The console's half of the Vibe app sign-in handshake.
 *
 * A browser that hits a private app is redirected here by the tenant's edge
 * authorizer, which knows nothing about who anyone is. The console authenticates
 * the person, runs the REAL permission check, and hands back a URL on the app's
 * own host that will set the session cookie. The only credential in this
 * response rides inside `callbackUrl` — a one-time code — which exists so the
 * cookie can be set on a host the console cannot reach.
 */

export const MintVibeAppSessionPathVarsSchema = z.object({
  id: z.string().uuid()
});

export const MintVibeAppSessionBodySchema = z.object({
  /**
   * The absolute URL the caller was trying to open, as the edge observed it and
   * passed to the console.
   *
   * **Attacker-supplied in full** — it travels through a redirect the edge did
   * not authenticate. The server checks it belongs to this app's own host and
   * refuses otherwise; it does not sanitise, because a mismatched origin is a
   * probe rather than a typo and quietly rewriting one hides it from the log.
   */
  returnTo: z.string().url(),
  /**
   * `"code"` when the edge that sent the browser here can redeem a one-time code
   * (it said so with `response_type=code` on the sign-in URL). Absent means an
   * edge that predates the code, which is still handed the legacy assertion URL
   * — see `VIBE_CALLBACK_ASSERTION_PARAM` for when that ends.
   *
   * Optional, so a console and a control plane on either side of the rollout
   * still agree: an older control plane strips the unknown key and answers the
   * way it always has.
   */
  responseType: z.literal("code").optional()
});

export const MintVibeAppSessionResponseSchema = z.object({
  /**
   * Absolute URL on the APP's host. The console navigates the browser here and is
   * finished — the tenant's authorizer recognises the path, redeems the one-time
   * code in it (or, for an edge that predates codes, verifies the assertion it
   * carries), sets the cookie and redirects on to where the user was going.
   *
   * Single-use and short-lived, but it IS a credential until it is redeemed:
   * navigate to it, never render it as a visible link or log it.
   */
  callbackUrl: z.string().url(),
  /** When the minted session stops being accepted, so the console can say so. */
  expiresAt: z.string().datetime()
});
